import {AdcConjugationMethod} from 'graphql-client/__generated/graphqlGlobalTypes';
import {CONJUGATION_METHOD_DISPLAY_NAME_TO_ENUM} from 'molbio/adcs/AdcConjugationConstants';
import {getAntibodyNumbering} from 'molbio/adcs/antibodyNumbering';
import {
  ConjugationSite,
  ConjugationSiteInputs,
} from 'molbio/adcs/conjugation-sites/AdcConjugationSiteConstants';
import {AntibodyDomain} from 'molbio/adcs/conjugation-sites/antibodyDomainsQuery';
import {getBioentityIdFromTypedValue} from 'notebook/tables/search-scoping/rowScopedSearch';
import {BaseTypedValueType, TypedValue} from 'notebook/tables/typed-values/TypedValueConstants';

/** The error for selected sites that are not conjugation sites for the row's protein and method. */
export function invalidConjugationSitesError(invalidSiteCount: number): string {
  return invalidSiteCount === 1
    ? '1 selected site is not a conjugation site for this protein and method.'
    : `${invalidSiteCount} selected sites are not conjugation sites for this protein and method.`;
}

/** Identifies a site; used as the site's dropdown option value. */
export function conjugationSiteKey({domainLabel, residueIndex}: ConjugationSite): string {
  return JSON.stringify([domainLabel, residueIndex]);
}

/**
 * Display label for a site's residue within its domain: the residue's antibody numbering (see
 * getAntibodyNumbering), e.g. "C220", or "C-idx:5" while the residue is unnumbered. Without the domain's sequence
 * (still loading, inaccessible, or no such domain) or past its end, the residue is shown by its 1-based position,
 * e.g. "idx:5".
 *
 * @param domains The antibody's domains (see useAntibodyDomains), or null if they aren't available
 */
export function formatConjugationSiteResidue(
  {domainLabel, residueIndex}: ConjugationSite,
  domains: ReadonlyArray<AntibodyDomain> | null = null
): string {
  const entity = domains?.find((domain) => domain.label === domainLabel)?.entity;
  if (entity == null || entity.__typename === 'InaccessibleInfoNode') {
    return `idx:${residueIndex + 1}`;
  }
  const {aminoAcids, antibodyNumberingInfo} = entity.experimentalCurrentVersion;
  return getAntibodyNumbering(aminoAcids, antibodyNumberingInfo?.numbering ?? null, residueIndex);
}

/**
 * Display label for a site: its domain, then its residue (see formatConjugationSiteResidue), e.g. "Hinge C220".
 *
 * @param domains The antibody's domains (see useAntibodyDomains), or null if they aren't available
 */
export function formatConjugationSite(
  site: ConjugationSite,
  domains: ReadonlyArray<AntibodyDomain> | null = null
): string {
  return `${site.domainLabel} ${formatConjugationSiteResidue(site, domains)}`;
}

/** Reads the row's Protein and Conjugation method cell values. */
export function getConjugationSiteInputs(
  proteinValue: TypedValue | null | undefined,
  conjugationMethodValue: TypedValue | null | undefined
): ConjugationSiteInputs {
  return {
    antibodyId: getBioentityIdFromTypedValue(proteinValue),
    conjugationMethod: getConjugationMethodFromTypedValue(conjugationMethodValue),
  };
}

export function getConjugationMethodFromTypedValue(
  typedValue: TypedValue | null | undefined
): AdcConjugationMethod | null {
  return typedValue?.type === BaseTypedValueType.STRING
    ? (CONJUGATION_METHOD_DISPLAY_NAME_TO_ENUM[typedValue.rawValue] ?? null)
    : null;
}
